import {Article, ArticlePart} from "@/model/Article";
import {plainTextForContentList, stripTagsAndEntities} from "@/utils/string";
import {isCompleteFaqPair} from "@/service/articleSanitize";
import {textMatchesArticleLanguage} from '@/service/articleLanguageFilter';
import {isChromeArticleBlock} from "@/utils/articleChrome";
import {FALLBACK_URL, getSuggestedUrl} from "@/utils/ArticleUrl";
import {generateArticleSchemaJsonLd, hasValidSchemaMarkup} from "@/service/seoScoreSchema";
import {I18n} from "@/plugins/I18n";

export type PreviewHeading = {
    id: string;
    level: number;
    text: string;
};

export type PreviewAuditItem = {
    id: string;
    ok: boolean;
    labelKey: string;
};

export type PreviewHtmlBundle = {
    fullHtml: string;
    bodyHtml: string;
    readerHtml: string;
    schemaJsonLd: string;
    markdown: string;
    headings: PreviewHeading[];
    title: string;
    readerH1: string;
    description: string;
    metaDescription: string;
    canonical: string;
    language: string;
    wordCount: number;
    schemaOk: boolean;
    audit: PreviewAuditItem[];
};

function unwrapInlineHtml(html: string): string {
    let value = String(html || '').trim();
    if (!value) {
        return '';
    }
    if (value.includes('&lt;') && !value.includes('<')) {
        value = value
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&#39;|&apos;/g, "'")
            .replace(/&amp;/g, '&');
    }
    return value
        .replace(/<\/?(?:p|div|h[1-6]|section|article|header|figure|figcaption)[^>]*>/gi, '')
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function wrapPreviewParagraph(html: string): string {
    const value = String(html || '').trim();
    if (!value) {
        return '';
    }
    if (/^<(p|ul|ol|blockquote|figure|div|h[1-6]|table)\b/i.test(value)) {
        return value;
    }
    return `<p>${value}</p>`;
}

function escapeAttr(value: string): string {
    return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

function prettySchema(article: Article): string {
    const raw = article.schemaJsonLd && hasValidSchemaMarkup(article.schemaJsonLd)
        ? article.schemaJsonLd
        : generateArticleSchemaJsonLd(article);
    try {
        return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
        return raw;
    }
}

function canonicalUrl(article: Article): string {
    if (article.url) return article.url;
    const keyword = article.selectedKeyword || article.keyword || 'clanek';
    return getSuggestedUrl(FALLBACK_URL, keyword);
}

function collectHeadings(article: Article): PreviewHeading[] {
    const headings: PreviewHeading[] = [];
    const langCode = article.language_code;
    let idx = 0;
    article.getAllParts().forEach((part: ArticlePart) => {
        part.value.forEach(block => {
            if (isChromeArticleBlock(block)) {
                return;
            }
            const headingText = plainTextForContentList(block.h);
            if (part.number > 1 && headingText && textMatchesArticleLanguage(headingText, langCode)) {
                headings.push({
                    id: `preview-h${part.number}-${idx}`,
                    level: part.number,
                    text: plainTextForContentList(block.h),
                });
            }
            idx += 1;
        });
    });
    const faqs = (article.questions || []).filter((item) => isCompleteFaqPair(item.question, item.answer));
    if (faqs.length) {
        const lang = (article.language_code || '').slice(0, 2).toLowerCase();
        headings.push({
            id: 'preview-faq',
            level: 2,
            text: lang === 'en' ? 'Frequently asked questions' : 'Často kladené otázky',
        });
        faqs.forEach((item, faqIndex) => {
            const q = stripTagsAndEntities(item.question);
            if (!textMatchesArticleLanguage(q, langCode)) {
                return;
            }
            headings.push({
                id: `preview-faq-${faqIndex}`,
                level: 3,
                text: q,
            });
        });
    }
    return headings;
}

function bodyInnerHtml(article: Article, headings: PreviewHeading[]): string {
    const headingIds = headings.map(item => item.id);
    let headingCursor = 0;
    const parts: string[] = [];
    article.getAllParts().forEach((part: ArticlePart) => {
        part.value.forEach(block => {
            if (isChromeArticleBlock(block)) {
                return;
            }
            if (block.h) {
                const id = part.number === 1 ? 'preview-h1' : (headingIds[headingCursor++] || '');
                const itemprop = part.number === 1 ? ' itemprop="headline"' : '';
                const idAttr = id ? ` id="${id}"` : '';
                parts.push(`<h${part.number}${idAttr}${itemprop}>${unwrapInlineHtml(block.h)}</h${part.number}>`);
            }
            if (block.p) {
                parts.push(wrapPreviewParagraph(block.p));
            }
        });
    });
    const faqs = (article.questions || []).filter((item) => isCompleteFaqPair(item.question, item.answer));
    if (faqs.length) {
        const lang = (article.language_code || '').slice(0, 2).toLowerCase();
        parts.push(`<h2 id="preview-faq">${lang === 'en' ? 'Frequently asked questions' : 'Často kladené otázky'}</h2>`);
        faqs.forEach((item, faqIndex) => {
            parts.push(`<h3 id="preview-faq-${faqIndex}">${unwrapInlineHtml(item.question)}</h3>`);
            parts.push(wrapPreviewParagraph(item.answer));
        });
    }
    const eatHtml = eatBoxHtml(article);
    if (eatHtml) {
        parts.push(eatHtml);
    }
    return parts.join('\n');
}

function eatBoxHtml(article: Article): string {
    const eat = article.eatAttribution;
    if (!eat || !eat.authorName) {
        return '';
    }
    const lang = (article.language_code || '').slice(0, 2).toLowerCase();
    const authorLabel = lang === 'en' ? 'Author' : 'Autor';
    const contribLabel = lang === 'en' ? 'Contributors' : 'Přispěvatelé';
    const date = eat.dateModified || eat.datePublished;
    const bits: string[] = [`<section class="eat-box" id="preview-eat">`];
    bits.push(`<p class="eat-byline"><strong>${authorLabel}:</strong> ${escapeAttr(eat.authorName)}`);
    if (eat.readMinutes) {
        bits.push(` · ${eat.readMinutes} min`);
    }
    if (date) {
        bits.push(` · ${escapeAttr(date)}`);
    }
    bits.push('</p>');
    if (eat.contributors) {
        bits.push(`<p><strong>${contribLabel}:</strong> ${escapeAttr(eat.contributors)}</p>`);
    }
    if (eat.authorBio) {
        bits.push(`<p>${escapeAttr(eat.authorBio)}</p>`);
    }
    bits.push('</section>');
    return bits.join('');
}

function toMarkdown(article: Article, title: string, description: string): string {
    const lines: string[] = [`# ${title}`];
    if (description) lines.push('', description);
    article.getAllParts().forEach((part: ArticlePart) => {
        part.value.forEach(block => {
            if (isChromeArticleBlock(block)) {
                return;
            }
            const heading = stripTagsAndEntities(block.h);
            const paragraph = stripTagsAndEntities(block.p);
            if (heading) {
                lines.push('', `${'#'.repeat(Math.min(part.number, 6))} ${heading}`);
            }
            if (paragraph) lines.push('', paragraph);
        });
    });
    const faqs = (article.questions || []).filter((item) => isCompleteFaqPair(item.question, item.answer));
    if (faqs.length) {
        const lang = (article.language_code || '').slice(0, 2).toLowerCase();
        lines.push('', `## ${lang === 'en' ? 'Frequently asked questions' : 'Často kladené otázky'}`);
        faqs.forEach(item => {
            const q = stripTagsAndEntities(item.question);
            const a = stripTagsAndEntities(item.answer);
            if (q) lines.push('', `### ${q}`);
            if (a) lines.push('', a);
        });
    }
    return lines.join('\n').trim() + '\n';
}

export function buildPreviewHtml(article: Article, wordCount: number): PreviewHtmlBundle {
    const readerH1 = stripTagsAndEntities(article.h1[0]?.h || article.selectedKeyword || article.keyword || '');
    const title = stripTagsAndEntities(article.metaTitle || readerH1 || '');
    const metaDescription = stripTagsAndEntities(article.metaDescription || '');
    const description = metaDescription || stripTagsAndEntities(article.h1[0]?.p || '');
    const language = (article.language_code || I18n.locale || 'cs').slice(0, 2);
    const canonical = canonicalUrl(article);
    const schemaJsonLd = prettySchema(article);
    const headings = collectHeadings(article);
    const inner = bodyInnerHtml(article, headings);
    const tocItems = headings.map(item => `<li><a href="#${item.id}">${escapeAttr(item.text)}</a></li>`).join('');
    const toc = headings.length
        ? `<nav class="toc"><strong>${I18n.t('article.contentList.title')}</strong><ol>${tocItems}</ol></nav>`
        : '';

    const readerLead = metaDescription
        ? `<p class="reader-meta-description" itemprop="description">${escapeAttr(metaDescription)}</p>`
        : '';

    const bodyHtml = [
        `<article itemscope itemtype="https://schema.org/BlogPosting">`,
        readerLead,
        toc,
        `<div itemprop="articleBody">`,
        inner,
        `</div>`,
        `</article>`,
    ].filter(Boolean).join('\n');

    const ogUrl = article.url || canonical;
    const published = article.created_at
        ? new Date(article.created_at).toISOString()
        : new Date().toISOString();

    const fullHtml = [
        '<!DOCTYPE html>',
        `<html lang="${escapeAttr(language)}">`,
        '<head>',
        '  <meta charset="UTF-8">',
        '  <meta name="viewport" content="width=device-width, initial-scale=1.0">',
        `  <title>${escapeAttr(title)}</title>`,
        `  <meta name="description" content="${escapeAttr(description)}">`,
        `  <link rel="canonical" href="${escapeAttr(canonical)}">`,
        '  <!-- Open Graph / Facebook / LinkedIn -->',
        '  <meta property="og:type" content="article">',
        `  <meta property="og:title" content="${escapeAttr(title)}">`,
        `  <meta property="og:description" content="${escapeAttr(description)}">`,
        `  <meta property="og:url" content="${escapeAttr(ogUrl)}">`,
        `  <meta property="article:published_time" content="${escapeAttr(published)}">`,
        `  <script type="application/ld+json">\n${schemaJsonLd}\n  </script>`,
        '</head>',
        '<body>',
        bodyHtml,
        '</body>',
        '</html>',
        '',
    ].join('\n');

    const hasFaq = (article.questions || []).some((item) => isCompleteFaqPair(item.question, item.answer));
    const hasImageAlt = (article.images || []).some(image => !!(image.alt && image.alt.trim()));
    const schemaOk = hasValidSchemaMarkup(schemaJsonLd);

    const audit: PreviewAuditItem[] = [
        {id: 'schema', ok: schemaOk, labelKey: 'article.previewDialog.audit.schema'},
        {id: 'blogposting', ok: schemaJsonLd.includes('BlogPosting'), labelKey: 'article.previewDialog.audit.blogPosting'},
        {id: 'headline', ok: !!title, labelKey: 'article.previewDialog.audit.headline'},
        {id: 'description', ok: !!description, labelKey: 'article.previewDialog.audit.description'},
        {id: 'canonical', ok: !!article.url || !!canonical, labelKey: 'article.previewDialog.audit.canonical'},
        {id: 'openGraph', ok: true, labelKey: 'article.previewDialog.audit.openGraph'},
        {id: 'h1', ok: !!readerH1, labelKey: 'article.previewDialog.audit.h1'},
        {id: 'faq', ok: !article.questions?.length || hasFaq, labelKey: 'article.previewDialog.audit.faq'},
        {id: 'images', ok: !(article.images || []).length || hasImageAlt, labelKey: 'article.previewDialog.audit.images'},
        {id: 'lang', ok: !!language, labelKey: 'article.previewDialog.audit.lang'},
    ];

    return {
        fullHtml,
        bodyHtml,
        readerHtml: inner.replace(/^<h1\b[^>]*>[\s\S]*?<\/h1>\s*/i, ''),
        schemaJsonLd,
        markdown: toMarkdown(article, title, description),
        headings,
        title,
        readerH1,
        description: metaDescription || description,
        metaDescription,
        canonical,
        language,
        wordCount,
        schemaOk,
        audit,
    };
}

export function countUtf8Bytes(text: string): number {
    return new TextEncoder().encode(text).length;
}
